import { createContext, useEffect } from 'react';
import { renderOptionsSlice } from '../render/options/renderOptionsStore';
import { combineReducers } from 'redux';
import { columnSlice } from '../render/columns/columnStore';
import { handlersSlice } from '../handlers/handlersStore';
import { reconciliationSlice } from '../reconciliation/reconciliationStore';
import { renderComponentsSlice } from '../render/renderComponents/renderComponentsStore';
import { configureStore, createAction } from '@reduxjs/toolkit';
import { errorSlice } from '../error/errorStore';
import { bulkActionsSlice } from '../bulkActions/bulkActionsStore';
import { Provider, createDispatchHook, createSelectorHook } from 'react-redux';
import { createStoreHook } from 'react-redux';
import { columnsDataSlice } from '../crmObjectBoardCore/columns/data/columnsDataSlice';
import { columnsRenderSlice } from '../crmObjectBoardCore/columns/render/columnsRenderSlice';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
export const resetState = createAction('globalActions/resetState');
const reducer = combineReducers({
  columns: columnSlice.reducer,
  handlers: handlersSlice.reducer,
  renderComponents: renderComponentsSlice.reducer,
  error: errorSlice.reducer,
  renderOptions: renderOptionsSlice.reducer,
  reconciliation: reconciliationSlice.reducer,
  bulkActions: bulkActionsSlice.reducer,
  columnsData: columnsDataSlice.reducer,
  columnsRender: columnsRenderSlice.reducer
});
const reducerProxy = (state, action) => {
  if (action.type === resetState.type) {
    // undefined causes reducers to reset to initialState
    return reducer(undefined, action);
  }
  return reducer(state, action);
};
const Context = /*#__PURE__*/createContext(
// @ts-expect-error Initializing to null for simplicity, a provider should always be present
null);
export const useStore = createStoreHook(Context);
export const useDispatch = createDispatchHook(Context);
export const useSelector = createSelectorHook(Context);
const store = configureStore({
  reducer: reducerProxy,
  preloadedState: {
    columns: columnSlice.getInitialState(),
    handlers: handlersSlice.getInitialState(),
    renderComponents: renderComponentsSlice.getInitialState(),
    error: errorSlice.getInitialState(),
    renderOptions: renderOptionsSlice.getInitialState(),
    reconciliation: reconciliationSlice.getInitialState(),
    bulkActions: bulkActionsSlice.getInitialState(),
    columnsData: columnsDataSlice.getInitialState(),
    columnsRender: columnsRenderSlice.getInitialState()
  },
  // We need to set the serializableCheck to false because we are storing non-serializable data like functions and components in the store.
  // Redux by default shows warnings in the console when it detects this since it prevents certain features like time travel debugging.
  // We don't really need these so we are safe to tell redux to ignore these checks.
  // See here: https://redux.js.org/faq/organizing-state#can-i-put-functions-promises-or-other-non-serializable-items-in-my-store-state
  middleware: getDefaultMiddleware => getDefaultMiddleware({
    serializableCheck: false
  })
});
export function useFastStore(selector) {
  return useSelector(selector);
}
export function useFastDispatch() {
  return useDispatch();
}
const FastContextInner = ({
  children
}) => {
  const dispatch = useFastDispatch();
  useEffect(() => {
    // We want to ensure that any hanging around redux state is cleaned up when the board unmounts
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
export function FastContext({
  children
}) {
  return /*#__PURE__*/_jsx(Provider, {
    store: store,
    context: Context,
    children: /*#__PURE__*/_jsx(FastContextInner, {
      children: children
    })
  });
}